import React, { useEffect } from 'react';

import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableGlobalConnectorPageFilterApplied,
  fetchAllGlobalConnectors,
  fetchGlobalConnectorPageFilters,
  refreshGlobalConnectors,
  setGlobalConnectorLimit,
  setGlobalConnectorPage,
  setGlobalConnectorSortBy,
  setGlobalConnectorSortField,
  updateGlobalConnectorPageFilters,
} from '../../../redux/slices/GlobalConnectorSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';

/**
 * Global connector management component — displays globally approved connectors.
 * @returns {React.JSX.Element} Global connector component
 */
function Global() {
  const dispatch = useAppDispatch();
  const {
    connectors,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.globalConnector);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllGlobalConnectors({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllGlobalConnectors({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableGlobalConnectorPageFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setGlobalConnectorSortBy(sortData.sortBy));
    dispatch(setGlobalConnectorSortField(sortData.sortField));
    dispatch(
      fetchAllGlobalConnectors({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setGlobalConnectorPage(value.page));
    if (value.limit !== undefined) dispatch(setGlobalConnectorLimit(value.limit));
    dispatch(
      fetchAllGlobalConnectors({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes global connector data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllGlobalConnectors({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes global connector data using current filters and toggle refresh
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      refreshGlobalConnectors({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  const column = [
    {
      label: 'Name',
      accessorKey: 'name',
      sortAllow: true,
      /**
       * Renders name
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.name || '-'}</span>,
    },
    {
      label: 'Current Type',
      accessorKey: 'currentType',
      /**
       * Renders connector type
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span className="current-type-label">{currentRow.currentType?.toUpperCase() || '-'}</span>
      ),
    },
    {
      label: 'Max Power Output (Kw)',
      accessorKey: 'capacityKW',
      sortAllow: true,
      /**
       * Renders max power output
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>{currentRow.capacityKW ? `${currentRow.capacityKW}` : '-'}</span>
      ),
    },
  ];

  return (
    <CustomTable
      dataKey="GlobalConnectorManagement"
      header={column}
      data={connectors}
      serialNo
      version="v2"
      loading={loading}
      page={page}
      limit={limit}
      height={350}
      created
      lastUpdated
      totalPages={totalPages}
      totalResults={totalResults}
      sortData={{ sortBy, sortField }}
      handleSortChange={handleSortFunction}
      pageLimitChange={handlePageLimitChange}
      sizeChangeButtonRequired
      filters={filters}
      filtersToDispatch={updateGlobalConnectorPageFilters}
      filterListLoading={filterListLoading}
      filterListToDispatch={fetchGlobalConnectorPageFilters}
      refreshRequired
      handleRefresh={handleRefresh}
      hoverLabel="Refresh"
      refershCurrentPages={handleRefresh}
      onHiddenColumnsChange={() => {}}
      requiredRefreshToggle
      refreshFunction={handleAutoRefresh}
    />
  );
}

export default Global;
